import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { ErrorBoundary } from "../ErrorBoundary";
import { Button } from "../ui/button";
import { ArrowLeft, Pencil, Upload, MapPin, Hash, Layers, DollarSign, Plus, Trash2, Tag, Copy, Database } from "lucide-react";
import { CedentesTab } from "./CedentesTab";
import { DocumentsTab } from "./DocumentsTab";
import { SignersTab } from "./SignersTab";
import { DashboardTab } from "./DashboardTab";
import { HerdeirosTab } from "./HerdeirosTab";
import { StatusImportDialog } from "./cedentes/StatusImportDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch.jsx";
import { processService } from "../../services/process.service";
import dataConnectionService from "../../services/dataConnection.service";
import { useActiveProvider } from "../../hooks/useActiveProvider";
import { cn } from "../../lib/utils";
import CrmEntityPanel from "./CrmEntityPanel";
import { ProcessContext } from "../../contexts/ProcessContext";
import { CondicoesEditor } from "./processo/CondicoesEditor";

const TABS = [
  { key: "cedentes",    label: "Substituídos" },
  { key: "documentos",  label: "Documentos"   },
  { key: "signatarios", label: "Signatários"  },
  { key: "dashboard",   label: "Dashboard"    },
  { key: "herdeiros",   label: "Herdeiros"    },
  { key: "crm",         label: "CRM"          },
];

// Tags genéricas sempre disponíveis; o provider adiciona as suas via available_tags
const BASE_DC_TAGS = ["{{cedente_nome}}", "{{cedente_cpf}}", "{{total_resultados}}", "{{data_consulta}}", "{{resultados}}"];

function DcConnectionCard({ conn, merged, isDirty, saving, onChange, onSave }) {
  const textareaRef = React.useRef(null);

  const providerTags = (conn.available_tags || []).map(t => `{{${t.tag}}}`);
  const tags = [...BASE_DC_TAGS, ...providerTags];

  const insertTag = (tag) => {
    const el = textareaRef.current;
    const current = merged.crm_template || "";
    if (!el) {
      onChange("crm_template", current + tag);
      return;
    }
    const start = el.selectionStart ?? current.length;
    const end = el.selectionEnd ?? current.length;
    const next = current.slice(0, start) + tag + current.slice(end);
    onChange("crm_template", next);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + tag.length, start + tag.length);
    });
  };

  return (
    <div className="border border-border rounded-lg p-4 space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-[11px] font-mono bg-statusInfoBg text-blue-600 border border-statusInfo/10 px-1.5 py-0.5 rounded">
          {conn.connection_type}
        </span>
        <span className="text-sm font-medium text-foreground">{conn.name}</span>
        <div className="ml-auto flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
            <button
              type="button"
              role="switch"
              aria-checked={merged.is_enabled}
              aria-label="Habilitar conexão"
              onClick={() => onChange("is_enabled", !merged.is_enabled)}
              className={cn(
                "relative inline-flex h-4 w-7 shrink-0 rounded-full border-2 border-transparent transition-colors",
                merged.is_enabled ? "bg-statusSuccess" : "bg-muted"
              )}
            >
              <span className={cn(
                "pointer-events-none inline-block h-3 w-3 rounded-full bg-card shadow-sm transform transition-transform",
                merged.is_enabled ? "translate-x-3" : "translate-x-0"
              )} />
            </button>
            Habilitado
          </label>
        </div>
      </div>
      <div className="space-y-1.5">
        <p className="text-xs text-muted-foreground">Mensagem CRM (enviada ao card quando a consulta é realizada)</p>
        <div className="flex flex-wrap gap-1 mb-1">
          {tags.map(tag => (
            <button
              key={tag}
              type="button"
              onClick={() => insertTag(tag)}
              className="text-[10px] font-mono bg-indigo-50 text-indigo-600 border border-indigo-100 px-1.5 py-0.5 rounded hover:bg-indigo-100 transition-colors"
            >
              {tag}
            </button>
          ))}
        </div>
        <textarea
          ref={textareaRef}
          value={merged.crm_template || ""}
          onChange={e => onChange("crm_template", e.target.value)}
          placeholder="Consulta realizada para {{cedente_nome}} ({{cedente_cpf}}) em {{data_consulta}}. Total: {{total_resultados}} resultado(s)."
          rows={3}
          className="w-full border border-border rounded-md px-3 py-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-blue-300 resize-none"
        />
      </div>
      <div className="flex justify-end">
        <Button
          size="sm"
          variant={isDirty ? "default" : "outline"}
          onClick={onSave}
          disabled={saving}
          className="h-7 text-xs gap-1"
        >
          {saving ? "Salvando…" : "Salvar"}
        </Button>
      </div>
    </div>
  );
}


function InfoChip({ icon: Icon, children }) {
  if (!children) return null;
  return (
    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
      <Icon className="w-3 h-3 shrink-0 text-muted-foreground" />
      {children}
    </span>
  );
}

export function ProcessDetail({ processo, onBack, onRefresh }) {
  const [activeTab, setActiveTab] = useState("cedentes");
  const [editDialog, setEditDialog] = useState(false);
  const [statusDialog, setStatusDialog] = useState(false);
  const { providers: allProviders } = useActiveProvider();
  const [editData, setEditData] = useState({
    nome_processo: processo?.nome_processo || "",
    cnj:           processo?.cnj           || "",
    vara:          processo?.vara          || "",
    uf:            processo?.uf            || "",
    tem_valores:                   processo?.tem_valores                   ?? true,
    parcelado:                     processo?.parcelado                     ?? true,
    calculo_automatico:            processo?.calculo_automatico            ?? true,
    tem_cessao:                    processo?.tem_cessao                    ?? true,
    meta_assinatura:               processo?.meta_assinatura               ?? 70,
    tipo_aceite_padrao:            processo?.tipo_aceite_padrao            ?? null,
    permite_atualizacao_bancaria:  processo?.permite_atualizacao_bancaria  ?? false,
    provider_id:                   processo?.provider_id                   ?? null,
    whatsapp_confirmacao:          processo?.whatsapp_confirmacao          ?? "",
    whatsapp_confirmacao_msg:      processo?.whatsapp_confirmacao_msg      ?? "",
    mensagem_contato_duvidas:      processo?.mensagem_contato_duvidas      ?? "",
    condicoes_cessao:              processo?.condicoes_cessao              ?? "",
    descricao:                     processo?.descricao                     ?? "",
    exibir_fgts:                   processo?.exibir_fgts                   ?? false,
    requer_data_nascimento:        processo?.requer_data_nascimento        ?? false,
    permitir_envio_documentos:     processo?.permitir_envio_documentos     ?? true,
    ativo:                         processo?.ativo                         ?? true,
    label_tipo_aceite_acordo:      processo?.label_tipo_aceite_acordo      ?? "",
    label_tipo_aceite_cessao:      processo?.label_tipo_aceite_cessao      ?? "",
  });
  const [saving, setSaving] = useState(false);

  // campos_valores editor
  const [campos, setCampos] = useState(processo?.campos_valores || []);
  const [newCampo, setNewCampo] = useState({ key: "", label: "", tag: "", negativo: false });
  const [savingCampos, setSavingCampos] = useState(false);

  // Data connections
  const [dcConnections, setDcConnections] = useState([]);
  const [dcConfigs, setDcConfigs] = useState({});   // keyed by connection.id
  const [dcDirty, setDcDirty] = useState({});       // keyed by connection.id
  const [savingDcId, setSavingDcId] = useState(null);

  const loadDcData = useCallback(async () => {
    try {
      const [conns, configs] = await Promise.all([
        dataConnectionService.list().catch(() => []),
        dataConnectionService.getProcessConfigs(processo.id).catch(() => []),
      ]);
      setDcConnections((conns || []).filter(c => c.is_active));
      const configMap = {};
      (configs || []).forEach(cfg => { configMap[cfg.data_connection_id] = cfg; });
      setDcConfigs(configMap);
    } catch {
      // silent — section simply stays empty
    }
  }, [processo.id]);

  useEffect(() => { loadDcData(); }, [loadDcData]);

  const handleDcChange = (connId, field, value) => {
    setDcDirty(prev => ({
      ...prev,
      [connId]: { ...(prev[connId] || dcConfigs[connId] || {}), [field]: value },
    }));
  };

  const handleSaveDc = async (connId) => {
    setSavingDcId(connId);
    try {
      const merged = { ...(dcConfigs[connId] || {}), ...(dcDirty[connId] || {}) };
      const payload = {
        is_enabled: merged.is_enabled ?? true,
        crm_template: merged.crm_template ?? null,
      };
      await dataConnectionService.saveProcessConfig(processo.id, connId, payload);
      toast.success("Configuração salva");
      await loadDcData();
      setDcDirty(prev => { const n = { ...prev }; delete n[connId]; return n; });
    } catch (e) {
      toast.error(e.message || "Erro ao salvar");
    } finally {
      setSavingDcId(null);
    }
  };

  const handleAddCampo = () => {
    const k = newCampo.key.trim();
    if (!k || !newCampo.label.trim()) return;
    const tag = newCampo.tag.trim() || `VALOR_${k.toUpperCase()}`;
    setCampos(prev => [...prev, { key: k, label: newCampo.label.trim(), tag, negativo: newCampo.negativo }]);
    setNewCampo({ key: "", label: "", tag: "", negativo: false });
  };

  const handleRemoveCampo = (idx) => setCampos(prev => prev.filter((_, i) => i !== idx));

  const handleSaveCampos = async () => {
    setSavingCampos(true);
    try {
      await processService.updateProcesso(processo.id, { campos_valores: campos });
      toast.success("Campos salvos");
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error("Erro ao salvar campos: " + e.message);
    } finally {
      setSavingCampos(false);
    }
  };

  if (!processo) return null;

  const handleUpdate = async () => {
    setSaving(true);
    try {
      await processService.updateProcesso(processo.id, editData);
      toast.success("Processo atualizado");
      setEditDialog(false);
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error("Erro ao atualizar: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto space-y-6">

      {/* Breadcrumb / nav */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Gestão
        </button>
        <span>/</span>
        <span className="text-muted-foreground font-medium truncate max-w-[260px]">
          {processo.nome_processo}
        </span>
      </div>

      {/* Process header card */}
      <div className="bg-card rounded-xl border border-border shadow-sm px-6 py-5">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-2 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-xl font-bold text-foreground leading-tight">
                {processo.nome_processo}
              </h2>
              <span className={cn(
                "inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs font-medium",
                processo.tem_valores
                  ? "bg-statusSuccessBg text-statusSuccess border-statusSuccess/10"
                  : "bg-muted text-muted-foreground border-border"
              )}>
                <DollarSign className="w-3 h-3" />
                {processo.tem_valores ? "Com Valores" : "Sem Valores"}
              </span>
              <span className={cn(
                "inline-flex items-center px-2 py-0.5 rounded border text-xs font-medium",
                (processo.parcelado ?? true)
                  ? "bg-sky-50 text-sky-700 border-sky-100"
                  : "bg-muted text-muted-foreground border-border"
              )}>
                {(processo.parcelado ?? true) ? "Parcelado" : "À Vista"}
              </span>
              <span className={cn(
                "inline-flex items-center px-2 py-0.5 rounded border text-xs font-medium",
                (processo.tem_cessao ?? true)
                  ? "bg-teal-50 text-teal-700 border-teal-100"
                  : "bg-muted text-muted-foreground border-border"
              )}>
                {(processo.tem_cessao ?? true) ? "Com Cessão" : "Sem Cessão"}
              </span>
              <span className={cn(
                "inline-flex items-center px-2 py-0.5 rounded border text-xs font-medium",
                (processo.calculo_automatico ?? true)
                  ? "bg-violet-50 text-violet-700 border-violet-100"
                  : "bg-orange-50 text-orange-700 border-orange-100"
              )}>
                {(processo.calculo_automatico ?? true) ? "Proposta Automática" : "Proposta Manual"}
              </span>
              <div className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${
                (processo.permite_atualizacao_bancaria ?? false)
                  ? "bg-teal-50 text-teal-700 border border-teal-200"
                  : "bg-muted text-muted-foreground border border-border"
              }`}>
                {(processo.permite_atualizacao_bancaria ?? false) ? "Dados Bancários Editáveis" : "Dados Bancários Fixos"}
              </div>
            </div>
            <div className="flex flex-wrap gap-4">
              <InfoChip icon={Hash}>
                {processo.cnj || "CNJ não informado"}
              </InfoChip>
              <InfoChip icon={MapPin}>
                {[processo.vara, processo.uf].filter(Boolean).join(" · ") || "Localização não informada"}
              </InfoChip>
              <InfoChip icon={Layers}>
                ID #{processo.id}
              </InfoChip>
              <button
                onClick={() => {
                  const url = `${process.env.REACT_APP_API_URL}/public/processos/${processo.id}/cedentes`;
                  navigator.clipboard.writeText(url);
                  toast.success("Link público copiado");
                }}
                title="Copiar link público da API"
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-muted text-muted-foreground border border-border hover:bg-muted hover:text-foreground transition-colors"
              >
                <Copy className="w-3 h-3" />
                Link público
              </button>
              <button
                onClick={() => {
                  const portalUrl = process.env.REACT_APP_PORTAL_URL || window.location.origin;
                  const hostname = window.location.hostname;
                  const parts = hostname.split('.');
                  const isLocalhost = parts[parts.length - 1] === 'localhost';
                  const hasSubdomain = parts.length >= 3 || (parts.length === 2 && isLocalhost);
                  const slug = hasSubdomain ? parts[0] : null;
                  let base = portalUrl.replace(/\/$/, '');
                  if (slug && !base.includes(`://${slug}.`)) {
                    base = base.replace(/^(https?:\/\/)/, `$1${slug}.`);
                  }
                  const url = `${base}/${processo.id}/envio-documentos`;
                  navigator.clipboard.writeText(url);
                  toast.success("Link de recálculo copiado");
                }}
                title="Copiar link público de solicitação de recálculo"
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-statusWarningBg text-amber-600 border border-statusWarning/20 hover:bg-statusWarningBg hover:text-statusWarning transition-colors"
              >
                <Copy className="w-3 h-3" />
                Link Recálculo
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setStatusDialog(true)}
              className="gap-1.5 h-8 text-muted-foreground border-border hover:border-border text-xs"
            >
              <Upload className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Importar Status</span>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setEditDialog(true)}
              className="h-8 w-8 text-muted-foreground hover:text-foreground"
              title="Editar processo"
            >
              <Pencil className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Campos de Valor dinâmicos */}
      {processo.tem_valores && (
        <div className="bg-card rounded-xl border border-border shadow-sm px-6 py-5 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Tag className="w-4 h-4 text-muted-foreground" />
              <p className="text-sm font-semibold text-foreground">Campos de Valor</p>
            </div>
            <Button size="sm" variant="outline" onClick={handleSaveCampos} disabled={savingCampos} className="h-7 text-xs gap-1">
              {savingCampos ? "Salvando…" : "Salvar campos"}
            </Button>
          </div>

          {campos.length > 0 && (
            <div className="space-y-2">
              {campos.map((c, idx) => (
                <div key={idx} className="flex items-center gap-3 px-3 py-2 bg-muted rounded-lg border border-border text-xs">
                  <span className="font-mono text-muted-foreground w-28 shrink-0 truncate">{c.key}</span>
                  <span className="text-foreground flex-1 truncate">{c.label}</span>
                  {c.negativo && (
                    <span className="shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold bg-statusErrorBg text-red-500 border border-statusError/10">−Taxa</span>
                  )}
                  <span className="font-mono text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded">{`{{${c.tag}}}`}</span>
                  <button onClick={() => handleRemoveCampo(idx)} className="text-muted-foreground hover:text-red-500 transition-colors ml-1 shrink-0">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-2 items-end pt-1 border-t border-border">
            <div className="space-y-1 flex-1 min-w-0">
              <Label className="text-xs text-muted-foreground">Chave</Label>
              <Input
                placeholder="ex: isonomia"
                value={newCampo.key}
                onChange={e => {
                  const k = e.target.value
                    .normalize("NFD").replace(/[̀-ͯ]/g, "")
                    .toLowerCase().replace(/\s+/g, "_");
                  setNewCampo(prev => ({
                    ...prev,
                    key: k,
                    tag: prev.tag || `VALOR_${k.toUpperCase()}`,
                  }));
                }}
                className="h-8 text-xs font-mono"
              />
            </div>
            <div className="space-y-1 flex-1 min-w-0">
              <Label className="text-xs text-muted-foreground">Label</Label>
              <Input
                placeholder="ex: Valor Isonomia"
                value={newCampo.label}
                onChange={e => setNewCampo(prev => ({ ...prev, label: e.target.value }))}
                className="h-8 text-xs"
              />
            </div>
            <div className="space-y-1 w-44 shrink-0">
              <Label className="text-xs text-muted-foreground">Tag ClickSign</Label>
              <Input
                placeholder="ex: VALOR_ISONOMIA"
                value={newCampo.tag}
                onChange={e => setNewCampo(prev => ({ ...prev, tag: e.target.value.toUpperCase().replace(/\s+/g, "_") }))}
                className="h-8 text-xs font-mono"
              />
            </div>
            <div className="space-y-1 shrink-0">
              <Label className="text-xs text-muted-foreground block">Negativo</Label>
              <div className="h-8 flex items-center">
                <Switch
                  checked={newCampo.negativo}
                  onCheckedChange={v => setNewCampo(prev => ({ ...prev, negativo: v }))}
                  className="data-[state=checked]:bg-destructive"
                />
              </div>
            </div>
            <Button size="sm" onClick={handleAddCampo} disabled={!newCampo.key || !newCampo.label} className="h-8 shrink-0 gap-1">
              <Plus className="w-3.5 h-3.5" /> Adicionar
            </Button>
          </div>
          {campos.length === 0 && <p className="text-xs text-muted-foreground text-center py-1">Nenhum campo definido ainda.</p>}
        </div>
      )}

      {/* Conexões de Dados */}
      {dcConnections.length > 0 && (
        <div className="bg-card rounded-xl border border-border shadow-sm px-6 py-5 space-y-4">
          <div className="flex items-center gap-2">
            <Database className="w-4 h-4 text-muted-foreground" />
            <p className="text-sm font-semibold text-foreground">Mensagens para Conexões Externas</p>
          </div>
          <div className="space-y-5">
            {dcConnections.length === 0 && (
              <p className="text-xs text-muted-foreground">Nenhuma conexão de dados ativa. Vincule uma em Conexões de Dados.</p>
            )}
            {dcConnections.map(conn => {
              const dirty = dcDirty[conn.id];
              const merged = { is_enabled: true, crm_template: "", ...(dcConfigs[conn.id] || {}), ...(dirty || {}) };
              return (
                <DcConnectionCard
                  key={conn.id}
                  conn={conn}
                  merged={merged}
                  isDirty={!!dirty}
                  saving={savingDcId === conn.id}
                  onChange={(field, value) => handleDcChange(conn.id, field, value)}
                  onSave={() => handleSaveDc(conn.id)}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* Pill tab bar */}
      <div className="flex gap-1 bg-muted p-1 rounded-lg w-fit">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={cn(
              "px-4 py-1.5 rounded-md text-sm font-medium transition-all",
              activeTab === tab.key
                ? "bg-card text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <ProcessContext.Provider value={{ processo, refresh: onRefresh }}>
        <div>
          {activeTab === "cedentes"    && <ErrorBoundary><CedentesTab processoId={processo.id} temValores={processo.tem_valores ?? true} camposValores={campos} parcelado={processo.parcelado ?? true} calculoAutomatico={processo.calculo_automatico ?? true} tipoAceitePadrao={processo.tipo_aceite_padrao ?? null} /></ErrorBoundary>}
          {activeTab === "documentos"  && <ErrorBoundary><DocumentsTab /></ErrorBoundary>}
          {activeTab === "signatarios" && <ErrorBoundary><SignersTab /></ErrorBoundary>}
          {activeTab === "dashboard"   && <ErrorBoundary><DashboardTab processoId={processo.id} /></ErrorBoundary>}
          {activeTab === "herdeiros"   && <ErrorBoundary><HerdeirosTab processoId={processo.id} /></ErrorBoundary>}
          {activeTab === "crm" && (
            <ErrorBoundary>
              <div className="p-4 max-w-lg">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Vínculo CRM — Lista</p>
                <CrmEntityPanel entityType="processo" entityId={processo.id} onChanged={onRefresh} />
              </div>
            </ErrorBoundary>
          )}
        </div>
      </ProcessContext.Provider>

      {/* Edit dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="flex flex-col max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Editar Processo</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-4 py-2 pr-1">
            <div className="space-y-1.5">
              <Label>Nome do Processo</Label>
              <Input
                value={editData.nome_processo}
                onChange={e => setEditData({ ...editData, nome_processo: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>CNJ</Label>
              <Input
                value={editData.cnj}
                onChange={e => setEditData({ ...editData, cnj: e.target.value })}
                className="font-mono text-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Vara</Label>
                <Input
                  value={editData.vara}
                  onChange={e => setEditData({ ...editData, vara: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>UF</Label>
                <Input
                  value={editData.uf}
                  onChange={e => setEditData({ ...editData, uf: e.target.value })}
                  maxLength={2}
                  className="uppercase"
                />
              </div>
            </div>
            <div className="py-2 border-t border-border mt-2 space-y-1.5">
              <Label>Descrição do Processo</Label>
              <textarea
                value={editData.descricao}
                onChange={e => setEditData({ ...editData, descricao: e.target.value })}
                placeholder="Texto exibido abaixo do valor no card de cada substituído (ex: Valor estimado referente ao processo de Isonomia)"
                rows={2}
                className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 resize-none"
              />
              <p className="text-xs text-muted-foreground">Aparece como legenda abaixo do valor no formulário do substituído.</p>
            </div>
            <div className="flex items-center justify-between py-2 border-t border-border">
              <div>
                <p className="text-sm font-medium text-foreground">Exibir separação FGTS</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Quando ativo, exibe o valor de FGTS separado no card do processo e no totalizador de valores.
                </p>
              </div>
              <Switch
                checked={editData.exibir_fgts}
                onCheckedChange={val => setEditData({ ...editData, exibir_fgts: val })}
              />
            </div>
            <div className="flex items-center justify-between py-2 border-t border-border">
              <div>
                <p className="text-sm font-medium text-foreground">Possui Valores Financeiros</p>
                <p className="text-xs text-muted-foreground mt-0.5">Desative para ocultar campos de valor no cadastro e na tela do substituído</p>
              </div>
              <Switch
                checked={editData.tem_valores}
                onCheckedChange={val => setEditData({ ...editData, tem_valores: val })}
              />
            </div>
            <div className="flex items-center justify-between py-2 border-t border-border">
              <div>
                <p className="text-sm font-medium text-foreground">Pagamento Parcelado</p>
                <p className="text-xs text-muted-foreground mt-0.5">Desative para ocultar campos de parcelas, datas e períodos no cadastro</p>
              </div>
              <Switch
                checked={editData.parcelado}
                onCheckedChange={val => setEditData({ ...editData, parcelado: val })}
              />
            </div>
            <div className="flex items-center justify-between py-2 border-t border-border">
              <div>
                <p className="text-sm font-medium text-foreground">Habilitar Cessão / Anuência</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Quando ativo, permite adicionar documentos do tipo Cessão e Anuência ao processo
                  e habilita o fluxo de assinatura de cessão para os substituídos.
                  Desative para processos que trabalham apenas com acordo.
                </p>
              </div>
              <Switch
                checked={editData.tem_cessao}
                onCheckedChange={val => setEditData({ ...editData, tem_cessao: val })}
              />
            </div>
            <div className="flex items-center justify-between py-2 border-t border-border">
              <div>
                <p className="text-sm font-medium text-foreground">Cálculo Automático da Proposta</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Quando ativo, o sistema calcula automaticamente o valor da proposta final, valor à vista,
                  valor parcelado e valor de cada parcela com base no crédito e nos descontos informados.
                  Desative para que o operador preencha esses valores manualmente.
                </p>
              </div>
              <Switch
                checked={editData.calculo_automatico}
                onCheckedChange={val => setEditData({ ...editData, calculo_automatico: val })}
              />
            </div>
            <div className="flex items-center justify-between py-2 border-t border-border">
              <div>
                <p className="text-sm font-medium text-foreground">Atualização bancária</p>
                <p className="text-xs text-muted-foreground mt-0.5">Cedentes podem atualizar dados bancários após envio</p>
              </div>
              <Switch
                checked={editData.permite_atualizacao_bancaria}
                onCheckedChange={val => setEditData({ ...editData, permite_atualizacao_bancaria: val })}
              />
            </div>
            <div className="flex items-center justify-between py-2 border-t border-border">
              <div>
                <p className="text-sm font-medium text-foreground">Processo ativo</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Quando desativado, os substituídos não conseguem aceitar propostas deste processo
                  (o formulário exibe aviso de indisponibilidade).
                </p>
              </div>
              <Switch
                checked={editData.ativo}
                onCheckedChange={val => setEditData({ ...editData, ativo: val })}
              />
            </div>
            <div className="flex items-center justify-between py-2 border-t border-border">
              <div>
                <p className="text-sm font-medium text-foreground">Permitir envio de documentos</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Quando ativo, exibe o botão de envio de documentos para os substituídos deste processo,
                  mesmo sem a trava de recálculo. Desative para ocultá-lo nos casos não obrigatórios.
                </p>
              </div>
              <Switch
                checked={editData.permitir_envio_documentos}
                onCheckedChange={val => setEditData({ ...editData, permitir_envio_documentos: val })}
              />
            </div>
            <div className="flex items-center justify-between py-2 border-t border-border">
              <div>
                <p className="text-sm font-medium text-foreground">Validar data de nascimento no login</p>
                <p className="text-xs text-muted-foreground mt-0.5">Exige que o cedente informe a data de nascimento ao acessar o portal</p>
              </div>
              <Switch
                checked={editData.requer_data_nascimento}
                onCheckedChange={val => setEditData({ ...editData, requer_data_nascimento: val })}
              />
            </div>
            <div className="py-2 border-t border-border space-y-2">
              <div>
                <p className="text-sm font-medium text-foreground">Label do badge "Acordo"</p>
                <p className="text-xs text-muted-foreground mt-0.5">Texto exibido quando o cedente assinou o acordo (padrão: "Aceitou o Acordo")</p>
              </div>
              <input
                type="text"
                placeholder="Aceitou o Acordo"
                value={editData.label_tipo_aceite_acordo}
                onChange={e => setEditData({ ...editData, label_tipo_aceite_acordo: e.target.value })}
                className="w-full text-sm border border-border rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-300 text-muted-foreground"
              />
            </div>
            <div className="py-2 border-t border-border space-y-2">
              <div>
                <p className="text-sm font-medium text-foreground">Label do badge "Cessão"</p>
                <p className="text-xs text-muted-foreground mt-0.5">Texto exibido quando o cedente assinou a cessão (padrão: "Aceitou a Cessão")</p>
              </div>
              <input
                type="text"
                placeholder="Aceitou a Cessão"
                value={editData.label_tipo_aceite_cessao}
                onChange={e => setEditData({ ...editData, label_tipo_aceite_cessao: e.target.value })}
                className="w-full text-sm border border-border rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-300 text-muted-foreground"
              />
            </div>
            <div className="flex items-center justify-between py-2 border-t border-border">
              <div>
                <p className="text-sm font-medium text-foreground">Meta de Assinatura (%)</p>
                <p className="text-xs text-muted-foreground mt-0.5">Percentual alvo de substituídos que devem assinar neste processo</p>
              </div>
              <input
                type="number"
                min={1}
                max={100}
                value={editData.meta_assinatura ?? 70}
                onChange={e => setEditData({ ...editData, meta_assinatura: Math.min(100, Math.max(1, Number(e.target.value))) })}
                className="w-16 text-center text-sm font-semibold border border-border rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-300 text-muted-foreground"
              />
            </div>
            <div className="py-2 border-t border-border">
              <div className="space-y-1.5">
                <Label>Estratégia de Aceite Padrão</Label>
                <select
                  className="w-full border rounded px-2 py-1.5 text-sm"
                  value={editData.tipo_aceite_padrao ?? ''}
                  onChange={e => setEditData({ ...editData, tipo_aceite_padrao: e.target.value || null })}
                >
                  <option value="">— Livre (acordo e cessão) —</option>
                  <option value="acordo">Acordo</option>
                  <option value="cessao">Cessão</option>
                </select>
                <p className="text-xs text-muted-foreground">
                  Define o tipo de aceite padrão para novos vínculos e adapta os labels no cadastro de substituídos.
                </p>
              </div>
            </div>
            <div className="py-2 border-t border-border">
              <div className="space-y-1.5">
                <Label>Provedor de Assinatura</Label>
                <select
                  className="w-full border rounded px-2 py-1.5 text-sm"
                  value={editData.provider_id ?? ''}
                  onChange={e => setEditData({ ...editData, provider_id: e.target.value !== '' ? Number(e.target.value) : null })}
                >
                  <option value="">— Herdar do sindicato —</option>
                  {allProviders.map(p => (
                    <option key={p.id} value={p.id}>{p.display_name || p.name}</option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground">Deixe vazio para usar o provedor configurado no sindicato (ou o global)</p>
              </div>
            </div>
            <div className="py-2 border-t border-border space-y-3">
              <p className="text-sm font-medium text-foreground">WhatsApp de Confirmação</p>
              <div className="space-y-1.5">
                <Label>Número (sem +55)</Label>
                <Input
                  value={editData.whatsapp_confirmacao}
                  onChange={e => setEditData({ ...editData, whatsapp_confirmacao: e.target.value })}
                  placeholder="Ex: 11999999999"
                  className="font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground">Após o aceite, o substituído é redirecionado para este número. Deixe vazio para desativar.</p>
              </div>
              <div className="space-y-1.5">
                <Label>Mensagem Pré-definida</Label>
                <textarea
                  value={editData.whatsapp_confirmacao_msg}
                  onChange={e => setEditData({ ...editData, whatsapp_confirmacao_msg: e.target.value })}
                  placeholder="Mensagem que será enviada via WhatsApp após o aceite..."
                  rows={3}
                  className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 resize-none"
                />
                <p className="text-xs text-muted-foreground">Deixe vazio para usar mensagem padrão com nome e CPF.</p>
              </div>
            </div>

            <div className="py-2 border-t border-border space-y-3">
              <p className="text-sm font-medium text-foreground">Mensagem de Contato (tela final)</p>
              <div className="space-y-1.5">
                <Label>Texto exibido após a conclusão da assinatura</Label>
                <textarea
                  value={editData.mensagem_contato_duvidas}
                  onChange={e => setEditData({ ...editData, mensagem_contato_duvidas: e.target.value })}
                  placeholder="Ex: Dúvidas? Entre em contato: (11) 93333-3012"
                  rows={2}
                  className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 resize-none"
                />
                <p className="text-xs text-muted-foreground">Deixe vazio para usar a mensagem padrão do sistema.</p>
              </div>
            </div>

            {editData.tem_cessao && (
              <div className="py-2 border-t border-border space-y-2">
                <div>
                  <p className="text-sm font-medium text-foreground">Condições de Cessão de Crédito</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Texto exibido ao substituído na seção de condições do formulário de aceite.
                    Selecione palavras e use os botões da toolbar para formatar ou destacar termos importantes.
                  </p>
                </div>
                <CondicoesEditor
                  value={editData.condicoes_cessao}
                  onChange={val => setEditData({ ...editData, condicoes_cessao: val || null })}
                />
              </div>
            )}
          </div>
          <DialogFooter className="pt-2 border-t border-border">
            <Button variant="outline" onClick={() => setEditDialog(false)}>Cancelar</Button>
            <Button onClick={handleUpdate} disabled={saving || !editData.nome_processo}>
              {saving ? "Salvando…" : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <StatusImportDialog
        isOpen={statusDialog}
        onOpenChange={setStatusDialog}
        onSuccess={onRefresh}
        processoId={processo.id}
      />
    </div>
  );
}
